import React, { useEffect, useState } from 'react';

export default function OnlineOpeningRollModal({
  openingRoll,
  assignedColor,
  starterName,
  whiteName,
  blackName,
  onRoll,
  isRolling,
}) {
  const [animWhite, setAnimWhite] = useState(1);
  const [animBlack, setAnimBlack] = useState(1);

  useEffect(() => {
    if (!isRolling) return undefined;

    const timer = setInterval(() => {
      setAnimWhite(Math.floor(Math.random() * 6) + 1);
      setAnimBlack(Math.floor(Math.random() * 6) + 1);
    }, 90);

    return () => clearInterval(timer);
  }, [isRolling]);

  useEffect(() => {
    if (!openingRoll?.token) return;
    setAnimWhite(1);
    setAnimBlack(1);
  }, [openingRoll?.token]);

  if (!openingRoll) return null;

  const isAutoStart = Boolean(openingRoll.autoStart);
  const myRoll = assignedColor ? openingRoll?.[assignedColor] : null;
  const bothRolled = Number.isInteger(openingRoll.white) && Number.isInteger(openingRoll.black);
  const showResult = !openingRoll.active && Boolean(openingRoll.starter);
  const canRoll = openingRoll.active && !Number.isInteger(myRoll) && !bothRolled && !showResult && !isRolling;

  const displayWhite = Number.isInteger(openingRoll.white)
    ? openingRoll.white
    : (isRolling && assignedColor === 'white' ? animWhite : '-');
  const displayBlack = Number.isInteger(openingRoll.black)
    ? openingRoll.black
    : (isRolling && assignedColor === 'black' ? animBlack : '-');

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.badge}>{isAutoStart ? 'Next Round' : 'Opening Roll'}</div>
        <h2 style={styles.title}>{isAutoStart ? 'Starting Next Game' : 'Roll To Start'}</h2>
        <div style={styles.subtitle}>
          {isAutoStart
            ? 'Previous game winner will take the first move for this round.'
            : 'Each player rolls one die. Higher number gets the first move.'}
        </div>

        {!isAutoStart && (
          <div style={styles.scoreRow}>
            <div style={styles.playerCard}>
              <div style={styles.playerLabel}>{whiteName}</div>
              <div style={styles.rollValue}>{displayWhite}</div>
            </div>
            <div style={styles.vsPill}>vs</div>
            <div style={styles.playerCard}>
              <div style={styles.playerLabel}>{blackName}</div>
              <div style={styles.rollValue}>{displayBlack}</div>
            </div>
          </div>
        )}

        {!showResult && !isAutoStart && (
          <div style={styles.statusNote}>
            {bothRolled
              ? 'Deciding starter...'
                : myRoll
                ? 'Waiting for your opponent to roll.'
                : 'Tap the button below to roll your die.'}
          </div>
        )}

        {showResult ? (
          <>
            <div style={styles.winnerNote}>
              <b>{starterName}</b> will start the first move.
            </div>
            <div style={styles.autoCloseNote}>Starting game in 5 seconds...</div>
          </>
        ) : (
          <button
            type="button"
            onClick={onRoll}
            disabled={!canRoll}
            style={{
              ...styles.primaryBtn,
              ...(!canRoll ? styles.primaryBtnDisabled : {}),
            }}
          >
            {isRolling ? 'Rolling...' : myRoll ? 'Rolled' : 'Roll Dice'}
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7, 3, 1, 0.38)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1200,
  },
  card: {
    width: 'min(100%, 540px)',
    background: 'linear-gradient(180deg, rgba(46, 24, 13, 0.98), rgba(27, 14, 8, 0.98))',
    border: '1px solid rgba(215,177,106,0.38)',
    borderRadius: 18,
    boxShadow: '0 18px 48px rgba(0,0,0,0.42)',
    padding: 24,
    display: 'grid',
    gap: 16,
    textAlign: 'center',
  },
  badge: {
    justifySelf: 'center',
    border: '1px solid rgba(215,177,106,0.45)',
    background: 'rgba(122, 76, 34, 0.26)',
    color: '#f3d392',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  title: {
    margin: 0,
    color: '#f6e7c1',
    fontSize: 30,
    fontWeight: 800,
    lineHeight: 1.05,
  },
  subtitle: {
    color: '#d8c3a0',
    fontSize: 15,
    lineHeight: 1.5,
  },
  scoreRow: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    gap: 12,
    alignItems: 'center',
  },
  playerCard: {
    display: 'grid',
    gap: 8,
    padding: 16,
    borderRadius: 14,
    border: '1px solid rgba(215,177,106,0.22)',
    background: 'rgba(255,255,255,0.03)',
  },
  playerLabel: {
    color: '#d8c3a0',
    fontSize: 14,
    fontWeight: 700,
  },
  rollValue: {
    color: '#fff3d3',
    fontSize: 40,
    fontWeight: 900,
    lineHeight: 1,
    minHeight: 40,
  },
  vsPill: {
    color: '#d7b16a',
    fontSize: 18,
    fontWeight: 800,
  },
  statusNote: {
    color: '#e7c98a',
    fontSize: 16,
    lineHeight: 1.45,
    minHeight: 24,
  },
  winnerNote: {
    color: '#f6e7c1',
    fontSize: 18,
    lineHeight: 1.45,
  },
  autoCloseNote: {
    color: '#e7c98a',
    fontSize: 14,
    fontWeight: 700,
  },
  primaryBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 12,
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 800,
  },
  primaryBtnDisabled: {
    opacity: 0.62,
    cursor: 'default',
  },
};
